import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FiArrowLeft, FiBook, FiCheck, FiCopy, FiDownload, FiHeadphones, FiImage, FiKey, FiMic, FiMicOff, FiPlus, FiSend, FiSliders, FiSquare, FiThumbsDown, FiThumbsUp, FiVolume2, FiVolumeX } from 'react-icons/fi'
import * as authAction from '../store/authAction'
import BenRobot, { benWord, avatarCommand } from '../comps/benRobot'
import { benHistory, benImageUrl, benStatus, rateBen, saveBenAccount, showBen, talkToBen, teachBen } from '../data/ben_data'
import '../styles/ben.css'

const VOICE_KEY = 'ben-voice'
const DEFAULT_PREFS = { name: '', rate: 1, pitch: 1, on: true }

const PLATFORMS = [
  { id: 'x', label: 'X / Twitter', handle: 'Handle', token: 'Bearer token', post: true, say: 'post on X: hello' },
  { id: 'facebook', label: 'Facebook', handle: 'Page ID', token: 'Page access token', post: true, say: 'post on Facebook: hello' },
  { id: 'telegram', label: 'Telegram', handle: 'Chat ID or @channel', token: 'Bot token', post: true, say: 'post on Telegram: hello' },
  { id: 'discord', label: 'Discord', handle: 'Server name (optional)', token: 'Webhook URL', post: true, say: 'post on Discord: hello' },
  { id: 'slack', label: 'Slack', handle: 'Workspace (optional)', token: 'Webhook URL', post: true, say: 'post on Slack: hello' },
  { id: 'mastodon', label: 'Mastodon', handle: 'Server, like mastodon.social', token: 'Access token', post: true, say: 'post on Mastodon: hello' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'threads', label: 'Threads' },
  { id: 'pinweelo', label: 'Pinweelo' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'reddit', label: 'Reddit' },
  { id: 'pinterest', label: 'Pinterest' },
  { id: 'snapchat', label: 'Snapchat' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'bluesky', label: 'Bluesky' },
  { id: 'tumblr', label: 'Tumblr' },
  { id: 'medium', label: 'Medium' },
  { id: 'twitch', label: 'Twitch' }
]

const platformOf = (id) => PLATFORMS.find((item) => item.id === id) || PLATFORMS[0]

const PRESETS = [
  { label: 'Natural', rate: 1, pitch: 1 },
  { label: 'Deep', rate: 0.95, pitch: 0.6 },
  { label: 'Bright', rate: 1.05, pitch: 1.5 },
  { label: 'Calm', rate: 0.85, pitch: 0.95 },
  { label: 'Fast', rate: 1.4, pitch: 1 }
]

const loadPrefs = () => {
  try {
    return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(VOICE_KEY) || '{}') }
  } catch (err) {
    return DEFAULT_PREFS
  }
}

const speakText = (text, prefs, voices) => new Promise((resolve) => {
  if (!window.speechSynthesis) {
    resolve()
    return
  }
  window.speechSynthesis.cancel()
  const clean = String(text || '')
    .replace(/```[\s\S]*?```/g, ' code block. ')
    .replace(/https?:\/\/\S+/g, 'link')
    .replace(/[`*_#>]/g, '')
    .replace(/\s+/g, ' ')
    .slice(0, 900)
  const utter = new SpeechSynthesisUtterance(clean)
  const voice = voices.find((item) => item.name === prefs.name)
  if (voice) {
    utter.voice = voice
    utter.lang = voice.lang
  }
  utter.rate = prefs.rate
  utter.pitch = prefs.pitch
  utter.onboundary = (event) => {
    if (event.name && event.name !== 'word') return
    const match = clean.slice(event.charIndex || 0).match(/^\S+/)
    if (match) benWord(match[0])
  }
  utter.onend = resolve
  utter.onerror = resolve
  window.speechSynthesis.speak(utter)
})

const moodFor = (value) => {
  const said = String(value || '').toLowerCase()
  if (/\b(sorry|can't|cannot|unable|couldn't|don't know|do not know|failed|error)\b/.test(said)) return 'sad'
  if (/\b(haha|hehe|lol|funny|joke)\b/.test(said)) return 'laugh'
  if (/\b(love|thank you|thanks)\b/.test(said)) return 'love'
  if (/\b(wow|amazing|awesome|congrat)/.test(said)) return 'excited'
  if (/\b(cool|great|nice)\b/.test(said)) return 'cool'
  if (/\b(blush|shy|embarrass)/.test(said)) return 'shy'
  if (/\bhmm\b|\blet me think\b/.test(said)) return 'thinking'
  if (/\?\s*$/.test(said)) return 'curious'
  if (said.includes('!')) return 'happy'
  return 'smile'
}

const heardMoodFor = (value) => {
  const said = String(value || '').toLowerCase()
  if (/\b(sad|cry|crying|tired|lonely|bad|hate|angry|mad|sick|hurt)\b/.test(said)) return 'sad'
  if (/\b(haha|hehe|lol|funny|joke)\b/.test(said)) return 'laugh'
  if (/\b(love|thank you|thanks|miss you)\b/.test(said)) return 'love'
  if (/\b(wow|amazing|awesome|congrats|yay)\b/.test(said)) return 'excited'
  if (/\b(cool|nice|great)\b/.test(said)) return 'cool'
  if (/\b(hi|hello|hey|good morning|good evening)\b/.test(said)) return 'happy'
  return 'curious'
}

const Ben = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector((state) => state.authReduse || {})
  const [status, setStatus] = useState(null)
  const [messages, setMessages] = useState([])
  const [chips, setChips] = useState([])
  const [copiedId, setCopiedId] = useState(null)
  const copyTimer = useRef(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [listening, setListening] = useState(false)
  const [speaking, setSpeaking] = useState(false)
  const [live, setLive] = useState(false)
  const [prefs, setPrefs] = useState(loadPrefs)
  const [voices, setVoices] = useState([])
  const [showVoice, setShowVoice] = useState(false)
  const [showAccounts, setShowAccounts] = useState(false)
  const [showAttach, setShowAttach] = useState(false)
  const [mood, setMood] = useState('smile')
  const [moodAt, setMoodAt] = useState(0)
  const [said, setSaid] = useState('')
  const [heardText, setHeardText] = useState('')
  const [heardMood, setHeardMood] = useState('curious')
  const [heardAt, setHeardAt] = useState(0)
  const [handle, setHandle] = useState('')
  const [token, setToken] = useState('')
  const [network, setNetwork] = useState('x')
  const listRef = useRef(null)
  const composerRef = useRef(null)
  const recRef = useRef(null)
  const busyRef = useRef(false)
  const liveRef = useRef(false)
  const speakingRef = useRef(false)
  const prefsRef = useRef(prefs)
  const voicesRef = useRef(voices)
  const sendRef = useRef(null)

  prefsRef.current = prefs
  voicesRef.current = voices

  const updatePrefs = (patch) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch }
      try { localStorage.setItem(VOICE_KEY, JSON.stringify(next)) } catch (err) {}
      return next
    })
  }

  useEffect(() => {
    if (!window.speechSynthesis) return undefined
    const load = () => setVoices(window.speechSynthesis.getVoices() || [])
    load()
    window.speechSynthesis.onvoiceschanged = load
    return () => {
      window.speechSynthesis.onvoiceschanged = null
      window.speechSynthesis.cancel()
    }
  }, [])

  useEffect(() => () => {
    liveRef.current = false
    if (recRef.current) {
      try { recRef.current.abort() } catch (err) {}
    }
  }, [])

  useEffect(() => {
    const boot = async () => {
      const saved = localStorage.getItem('userdata')
      if (!saved) {
        navigate('/auth', { replace: true, state: { from: '/ben' } })
        return
      }
      try {
        const { id, pid } = JSON.parse(saved)
        await dispatch(authAction.auth(id, pid))
        const [info, hist] = await Promise.all([benStatus(), benHistory()])
        setStatus(info)
        setMessages((hist.messages || []).map((row) => ({ id: row.id, role: row.role, text: row.text, image: row.image })))
      } catch (err) {
        setError(err.message || 'Could not open Ben')
      }
    }
    boot()
  }, [dispatch, navigate])

  useEffect(() => {
    if (!listRef.current) return
    listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages, busy, chips])

  // Ben practices in the background; while he does, keep checking and post his report when it lands.
  const devRunning = Boolean(status && status.dev && status.dev.running)
  useEffect(() => {
    if (!devRunning) return undefined
    const timer = setInterval(async () => {
      try {
        const info = await benStatus()
        setStatus(info)
        if (info.dev && info.dev.running) return
        const hist = await benHistory()
        setMessages((current) => {
          const have = new Set(current.map((row) => row.id))
          const fresh = (hist.messages || []).filter((row) => row.role === 'ben' && !have.has(row.id))
          return fresh.length ? current.concat(fresh.map((row) => ({ id: row.id, role: row.role, text: row.text, image: row.image }))) : current
        })
      } catch (_) { /* try again on the next tick */ }
    }, 3000)
    return () => clearInterval(timer)
  }, [devRunning])

  // Let him start his own studying (if switched on) while the page stays open.
  const autoStudy = Boolean(status && status.study && status.study.auto)
  useEffect(() => {
    if (!autoStudy || devRunning) return undefined
    const timer = setInterval(async () => {
      try { setStatus(await benStatus()) } catch (_) { /* try again later */ }
    }, 5 * 60 * 1000)
    return () => clearInterval(timer)
  }, [autoStudy, devRunning])

  const stopSpeaking = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel()
    speakingRef.current = false
    setSpeaking(false)
  }

  const speak = async (value) => {
    if (!prefsRef.current.on) return
    speakingRef.current = true
    setSpeaking(true)
    await speakText(value, prefsRef.current, voicesRef.current)
    speakingRef.current = false
    setSpeaking(false)
  }

  const startListening = () => {
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Rec) {
      setError('This browser cannot hear you. Try Chrome or Safari, or type instead.')
      liveRef.current = false
      setLive(false)
      return
    }
    if (recRef.current) {
      try { recRef.current.abort() } catch (err) {}
    }
    const rec = new Rec()
    const voice = voicesRef.current.find((item) => item.name === prefsRef.current.name)
    rec.lang = (voice && voice.lang) || navigator.language || 'en-US'
    let heard = false
    rec.onresult = (event) => {
      const said = event.results[0] && event.results[0][0] && event.results[0][0].transcript
      if (said) {
        heard = true
        if (sendRef.current) sendRef.current(said)
      }
    }
    rec.onend = () => {
      setListening(false)
      if (liveRef.current && !heard) {
        window.setTimeout(() => {
          if (liveRef.current && !busyRef.current && !speakingRef.current) startListening()
        }, 400)
      }
    }
    rec.onerror = (event) => {
      setListening(false)
      if (event && (event.error === 'not-allowed' || event.error === 'service-not-allowed')) {
        liveRef.current = false
        setLive(false)
        setError('Allow the microphone so Ben can hear you.')
      }
    }
    recRef.current = rec
    setListening(true)
    try { rec.start() } catch (err) { setListening(false) }
  }

  const send = async (value) => {
    const message = String(value === undefined ? text : value).trim()
    if (!message || busyRef.current) return
    const local = avatarCommand(message)
    if (local) {
      stopSpeaking()
      setText('')
      setChips([])
      setError('')
      setHeardText(message)
      setHeardMood('happy')
      setHeardAt(Date.now())
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', text: message },
        { id: `b-local-${Date.now()}`, role: 'ben', text: local.reply }
      ])
      setMood(local.mood)
      setSaid(local.reply)
      setMoodAt(Date.now())
      await speak(local.reply)
      if (liveRef.current) startListening()
      return
    }
    busyRef.current = true
    stopSpeaking()
    setText('')
    setChips([])
    setBusy(true)
    setError('')
    setHeardText(message)
    setHeardMood(heardMoodFor(message))
    setHeardAt(Date.now())
    setMessages((prev) => [...prev, { id: `u-${Date.now()}`, role: 'user', text: message }])
    try {
      const result = await talkToBen(message)
      setMessages((prev) => [...prev, { id: result.id || `b-${Date.now()}`, role: 'ben', text: result.reply, image: result.image }])
      setChips(result.chips || [])
      setMood(moodFor(result.reply))
      setSaid(String(result.reply || ''))
      setMoodAt(Date.now())
      busyRef.current = false
      setBusy(false)
      benStatus().then(setStatus).catch(() => {})
      await speak(result.reply)
    } catch (err) {
      setError(err.message || 'Ben is quiet right now')
    } finally {
      busyRef.current = false
      setBusy(false)
      if (liveRef.current) startListening()
    }
  }
  sendRef.current = send

  const copyMessage = async (row) => {
    const value = String(row.text || '')
    if (!value) return
    let done = false
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value)
        done = true
      }
    } catch (e) { /* fall back below */ }
    if (!done) {
      const box = document.createElement('textarea')
      box.value = value
      box.setAttribute('readonly', '')
      box.style.position = 'fixed'
      box.style.opacity = '0'
      document.body.appendChild(box)
      box.select()
      try { done = document.execCommand('copy') } catch (e) { done = false }
      document.body.removeChild(box)
    }
    if (done) {
      setCopiedId(row.id)
      window.clearTimeout(copyTimer.current)
      copyTimer.current = window.setTimeout(() => setCopiedId(null), 1500)
    }
  }

  const rate = async (row, value) => {
    if (row.rated) return
    setMessages((prev) => prev.map((item) => (item.id === row.id ? { ...item, rated: value } : item)))
    try {
      const result = await rateBen(row.id, value)
      if (result.ask) {
        setMessages((prev) => [...prev, { id: `b-fix-${Date.now()}`, role: 'ben', text: result.msg, noRate: true }])
        speak(result.msg)
      }
      benStatus().then(setStatus).catch(() => {})
    } catch (err) {
      setError(err.message || 'Could not save that rating')
    }
  }

  const toggleMic = () => {
    if (listening && recRef.current) {
      try { recRef.current.abort() } catch (err) {}
      setListening(false)
      return
    }
    stopSpeaking()
    startListening()
  }

  const toggleLive = () => {
    if (liveRef.current) {
      liveRef.current = false
      setLive(false)
      setListening(false)
      stopSpeaking()
      if (recRef.current) {
        try { recRef.current.abort() } catch (err) {}
      }
      return
    }
    liveRef.current = true
    setLive(true)
    if (!prefsRef.current.on) updatePrefs({ on: true })
    prefsRef.current = { ...prefsRef.current, on: true }
    stopSpeaking()
    startListening()
  }

  const sayLine = (line) => {
    stopSpeaking()
    if (!prefsRef.current.on) return
    speakingRef.current = true
    setSpeaking(true)
    speakText(line, prefsRef.current, voicesRef.current).then(() => {
      speakingRef.current = false
      setSpeaking(false)
    })
  }

  const goBack = () => {
    stopSpeaking()
    if (window.history.length > 1) navigate(-1)
    else navigate('/')
  }

  const testVoice = () => {
    speakText('Hi, I am Ben. This is how I sound. Change my voice any time.', prefsRef.current, voicesRef.current)
  }

  const onFile = async (event) => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!file) return
    setBusy(true)
    setError('')
    try {
      const learned = await teachBen(file)
      setMessages((prev) => [...prev, {
        id: `b-learn-${Date.now()}`,
        role: 'ben',
        text: learned.kind === 'code'
          ? `I studied ${learned.name}: ${learned.pages} code pieces stored, and my code model trained on it. Ask me to write something similar.`
          : `I read ${learned.name} (${learned.pages} pages), stored it, and trained my own language model on it. Ask me about it.`
      }])
      setStatus(await benStatus())
    } catch (err) {
      setError(err.message || 'Could not learn that file')
    } finally {
      setBusy(false)
    }
  }

  // close the + menu when you tap anywhere else or press Escape
  useEffect(() => {
    if (!showAttach) return undefined
    const away = (event) => {
      if (composerRef.current && !composerRef.current.contains(event.target)) setShowAttach(false)
    }
    const esc = (event) => { if (event.key === 'Escape') setShowAttach(false) }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [showAttach])

  const onPicture = async (event) => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    if (!file || busyRef.current) return
    if (!/^image\//.test(file.type)) {
      setError('Pick a picture file (jpg, png, webp, gif).')
      return
    }
    busyRef.current = true
    stopSpeaking()
    setChips([])
    setBusy(true)
    setError('')
    try {
      const result = await showBen(file)
      setMessages((prev) => [
        ...prev,
        { id: `u-pic-${Date.now()}`, role: 'user', text: `Sent a picture: ${file.name}`, localUrl: result.previewUrl },
        { id: result.id, role: 'ben', text: result.reply }
      ])
      setChips(result.chips || [])
      setMood(moodFor(result.reply))
      setSaid(String(result.reply || ''))
      setMoodAt(Date.now())
      busyRef.current = false
      setBusy(false)
      benStatus().then(setStatus).catch(() => {})
      await speak(result.reply)
    } catch (err) {
      setError(err.message || 'Ben could not look at that picture')
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  const saveAccount = async (event) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await saveBenAccount({ network, handle, token })
      setToken('')
      setStatus(await benStatus())
      setMessages((prev) => [...prev, {
        id: `b-acc-${Date.now()}`,
        role: 'ben',
        text: `I stored the ${platformOf(network).label} account in my brain.${platformOf(network).post ? ` Say "${platformOf(network).say}" and I will post it.` : ' I cannot post there by myself yet, but I will keep it safe.'}`
      }])
    } catch (err) {
      setError(err.message || 'Could not save that account')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="benPage">
      <Helmet>
        <title>Ben</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <header className="benTop">
        <button type="button" className="benBackBtn" onClick={goBack} aria-label="Go back">
          <FiArrowLeft />
        </button>
        <h1 className="benSrOnly">Ben</h1>
        <div className="benTitle" />
        <div className="benTopActions">
          <button
            type="button"
            className={showAccounts ? 'isOn' : ''}
            onClick={() => setShowAccounts((v) => !v)}
            aria-label={showAccounts ? 'Hide accounts Ben may use' : 'Show accounts Ben may use'}
            aria-expanded={showAccounts}
            title="Accounts Ben may use"
          >
            <FiKey />
          </button>
          <button type="button" className={live ? 'isOn' : ''} onClick={toggleLive} aria-label="Live conversation" title="Live conversation (hands-free)">
            <FiHeadphones />
          </button>
          <button type="button" className={showVoice ? 'isOn' : ''} onClick={() => setShowVoice((v) => !v)} aria-label="Voice settings" title="Change voice">
            <FiSliders />
          </button>
          <button type="button" className={prefs.on ? 'isOn' : ''} onClick={() => { if (prefs.on) stopSpeaking(); updatePrefs({ on: !prefs.on }) }} aria-label="Voice replies" title="Spoken replies">
            {prefs.on ? <FiVolume2 /> : <FiVolumeX />}
          </button>
        </div>
      </header>

      {showVoice && (
        <section className="benVoicePanel" aria-label="Voice settings">
          <label>
            <span>Voice</span>
            <select value={prefs.name} onChange={(event) => updatePrefs({ name: event.target.value })}>
              <option value="">Browser default</option>
              {voices.map((item) => (
                <option key={`${item.name}-${item.lang}`} value={item.name}>{item.name} ({item.lang})</option>
              ))}
            </select>
          </label>
          <div className="benPresets">
            {PRESETS.map((item) => (
              <button
                key={item.label}
                type="button"
                className={prefs.rate === item.rate && prefs.pitch === item.pitch ? 'isOn' : ''}
                onClick={() => updatePrefs({ rate: item.rate, pitch: item.pitch })}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label>
            <span>Speed {prefs.rate.toFixed(2)}</span>
            <input type="range" min="0.6" max="1.6" step="0.05" value={prefs.rate} onChange={(event) => updatePrefs({ rate: Number(event.target.value) })} />
          </label>
          <label>
            <span>Pitch {prefs.pitch.toFixed(2)}</span>
            <input type="range" min="0.4" max="1.8" step="0.05" value={prefs.pitch} onChange={(event) => updatePrefs({ pitch: Number(event.target.value) })} />
          </label>
          <button type="button" className="benTest" onClick={testVoice}>Hear this voice</button>
        </section>
      )}

      {showAccounts && (
        <form className="benAccounts" onSubmit={saveAccount}>
          <h2><FiKey /> Accounts Ben may use</h2>
          <p>Stored only in your brain folder on this server. Use an API token, not a password when you can.</p>
          <div>
            <select value={network} onChange={(event) => setNetwork(event.target.value)} aria-label="Platform">
              <optgroup label="Ben can post here">
                {PLATFORMS.filter((item) => item.post).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </optgroup>
              <optgroup label="Saved for later">
                {PLATFORMS.filter((item) => !item.post).map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </optgroup>
            </select>
            <input value={handle} onChange={(event) => setHandle(event.target.value)} placeholder={platformOf(network).handle || 'Handle'} />
            <input type="password" autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} placeholder={platformOf(network).token || 'Access token'} />
            <button type="submit" disabled={busy}>Save</button>
          </div>
          <p className="benAccountHint">
            {platformOf(network).post
              ? `Ben can post here. Say: "${platformOf(network).say}"`
              : 'Saved in his brain for later. Ben cannot post here by himself yet.'}
          </p>
        </form>
      )}

      <BenRobot
        speaking={speaking}
        listening={listening}
        thinking={busy}
        studying={devRunning && ['study', 'school', 'browse'].includes(status.dev.kind)}
        typing={Boolean(text.trim()) && !busy}
        mood={mood}
        moodAt={moodAt}
        said={said}
        heardText={heardText}
        heardMood={heardMood}
        heardAt={heardAt}
        voiced={Boolean(prefs.on && window.speechSynthesis)}
        onSay={sayLine}
      />

      <p className="benLead">
        {status
          ? `${status.memory} memories · ${status.facts} facts · ${status.pages} book pages · ${status.lessons} lessons · own model: ${(status.model?.words || 0).toLocaleString()} words, ${(status.model?.codeTokens || 0).toLocaleString()} code tokens · ${status.codePieces || 0} code pieces${status.vision && status.vision.ready ? ` · eyes: ${status.vision.labels} kinds, ${Math.round(status.vision.heldOut * 100)}% on new drawings of his own${status.vision.taught ? `, ${status.vision.taught} taught by you` : ''}` : ''}${status.nn ? ` · neural net: ${status.nn.params.toLocaleString()} weights, ${Math.round((status.nn.accuracy || 0) * 100)}% on its training set` : ''}${status.skill?.last ? ` · coding exam ${status.skill.last.passed}/${status.skill.last.total}` : ''}${status.dev?.running ? ` · practicing: ${status.dev.step}…` : ''}`
          : 'Loading brain…'}
        {live ? ' · Live: just talk, Ben listens after he answers' : ''}
      </p>

      <div className="benStream" ref={listRef}>
        {messages.length === 0 && (
          <div className="benEmpty">
            Say hi. I keep every conversation in my brain folder, learn from the books you give me, and remember what you tell me.
          </div>
        )}
        {messages.map((row) => (
          <article key={row.id} className={`benBubble is-${row.role}`}>
            <small>{row.role === 'ben' ? 'Ben' : user.id || 'You'}</small>
            {(row.localUrl || row.image) && (
              <div className="benPicWrap">
                <img className="benPic" src={row.localUrl || benImageUrl(row.image.id)} alt={row.role === 'ben' ? 'Picture Ben made' : 'Picture you sent'} loading="lazy" />
                {row.role === 'ben' && row.image && row.image.made !== false && (
                  <a className="benPicSave" href={benImageUrl(row.image.id)} download={`ben-picture-${row.image.id}.png`} title="Save this picture"><FiDownload /> Save</a>
                )}
              </div>
            )}
            <p>{row.text}</p>
            <div className="benRate">
              {row.text ? (
                <button type="button" className={copiedId === row.id ? 'isOn' : ''} onClick={() => copyMessage(row)} aria-label="Copy message" title={copiedId === row.id ? 'Copied' : 'Copy message'}>
                  {copiedId === row.id ? <FiCheck /> : <FiCopy />}
                </button>
              ) : null}
              {row.role === 'ben' && !row.noRate && !String(row.id).startsWith('b-learn') && !String(row.id).startsWith('b-acc') && (
                <>
                  <button type="button" className={row.rated === 1 ? 'isOn' : ''} onClick={() => rate(row, 1)} aria-label="Good answer" title="Good answer, learn from it"><FiThumbsUp /></button>
                  <button type="button" className={row.rated === -1 ? 'isOn' : ''} onClick={() => rate(row, -1)} aria-label="Wrong answer" title="Wrong, teach me the right one"><FiThumbsDown /></button>
                </>
              )}
            </div>
          </article>
        ))}
        {busy ? <p className="benBusy">Ben is thinking…</p> : null}
        {listening ? <p className="benBusy">Listening…</p> : null}
        {chips.length > 0 && !busy && (
          <div className="benChips">
            {chips.map((chip) => (
              <button key={chip} type="button" onClick={() => send(chip)}>{chip}</button>
            ))}
          </div>
        )}
      </div>

      {error ? <p className="benError">{error}</p> : null}

      <form ref={composerRef} className="benComposer" onSubmit={(event) => { event.preventDefault(); send() }}>
        {showAttach && (
          <div className="benAttachMenu" role="menu">
            <label role="menuitem">
              <FiImage />
              <span><b>Show a picture</b><small>Ben describes it and learns what you call it</small></span>
              <input type="file" accept="image/*" onChange={(event) => { onPicture(event); setShowAttach(false) }} />
            </label>
            <label role="menuitem">
              <FiBook />
              <span><b>Teach a book or file</b><small>Text, notes or code for Ben to study</small></span>
              <input type="file" accept=".txt,.md,.markdown,.csv,.json,.js,.jsx,.mjs,.ts,.tsx,.html,.htm,.css,.py,.log" onChange={(event) => { onFile(event); setShowAttach(false) }} />
            </label>
          </div>
        )}
        <button type="button" className={`benIconBtn${showAttach ? ' isOn' : ''}`} onClick={() => setShowAttach((open) => !open)} aria-label="Add a picture or a book" aria-expanded={showAttach}>
          <FiPlus />
        </button>
        <input
          className="benText"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Talk to Ben"
          maxLength={8000}
          enterKeyHint="send"
          autoComplete="off"
        />
        <button type="button" className={`benIconBtn${listening ? ' isOn' : ''}`} onClick={toggleMic} aria-label="Talk">
          {listening ? <FiMicOff /> : <FiMic />}
        </button>
        {speaking ? (
          <button type="button" className="benSendBtn isOn" onClick={stopSpeaking} aria-label="Stop talking"><FiSquare /></button>
        ) : (
          <button type="submit" className={`benSendBtn${text.trim() && !busy ? ' isOn' : ''}`} disabled={busy || !text.trim()} aria-label="Send"><FiSend /></button>
        )}
      </form>
    </main>
  )
}

export default Ben
